/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useProject } from 'src/features/projects/hooks/useProject';
import { useProjectAccess, hasPerm } from 'src/features/projects/hooks/useProjectAccess';
import { ProjectAccessProvider } from 'src/features/projects/ProjectAccessContext';
import { ProjectSettingProvider } from 'src/features/projects/contexts/ProjectSettingContext';
import type { ProjectSetting } from 'src/features/projects/types';
import { useAuth } from 'src/features/auth/AuthContext';
import { useWbsCurrent } from './repo';
import WbsProgressEntryContainer from './WbsProgressEntryContainer';

export default function WbsProgressEntryPage() {
    const { projectId } = useParams<{ projectId: string }>();

    if (!projectId?.trim()) {
        return <div className="p-6 text-sm text-rose-700">잘못된 프로젝트 ID 입니다.</div>;
    }

    const { me, logout } = useAuth();
    const { data: projectRes } = useProject(projectId);

    const { data: accessRes, isLoading: accessLoading } = useProjectAccess(projectId);
    const access = accessRes?.data ?? null;

    const canEvEntry = !accessLoading && hasPerm(access?.permissions, 'wbs.ev.entry');
    const actorProjectCompanyId = access?.actorProjectCompanyId ?? null;
    const canUseEntryPage = !accessLoading && !!access && canEvEntry && !!actorProjectCompanyId;

    // console.log('actorProjectCompanyId: ', actorProjectCompanyId);
    // console.log('canEvEntry: ', canEvEntry);
    // 외부 사용자용 fallback setting
    const setting: ProjectSetting = {
        id: 'external-fallback',
        projectId,
        timeZone: projectRes?.data?.timeZone ?? 'Asia/Seoul',
        wbsVisibilityMode: 'HIDE_UNASSIGNED_FOR_COMPANY',
        companyWbsDefaultView: 'ONLY_ASSIGNED',
        wbsEvApproveBeforeClose: false,
        wbsWeekAnchorDow: 5,
        wbsWeekAnchorDowPending: null,
        wbsWeekAnchorEffectiveFrom: null,
    };

    const asOf = useMemo(() => new Date(), []);
    const { data: currentWbs, isLoading: currentWbsLoading } = useWbsCurrent(projectId, {
        viewAs: 'WEEK',
        at: asOf,
    });

    const hasCurrentWbs = (currentWbs?.items?.length ?? 0) > 0;
    const projectName = projectRes?.data?.name ?? 'Project';

    // 현재 프론트에서는 이 플래그를 외부 사용자 판정의 기준으로 사용
    // const isExternalEntryUser =
    //     !accessLoading && !!access && access.isExternalProjectCompanyUser === true && access.isOwnerCompanyUser !== true && canEvEntry;

    if (accessLoading) {
        return <div className="p-6 text-sm text-gray-500">프로젝트 접근 권한 확인 중…</div>;
    }

    // 내부 사용자는 일반 WBS로 보냄
    // if (!isExternalEntryUser) {
    //     return <Navigate to={`/projects/${projectId}/wbs`} replace />;
    // }

    if (!canUseEntryPage) {
        return <Navigate to={`/projects/${projectId}/wbs`} replace />;
    }

    return (
        <ProjectAccessProvider access={access} isLoading={false}>
            <ProjectSettingProvider setting={setting} isLoading={false}>
                <div className="w-full min-h-screen bg-gray-50">
                    <header className="border-b bg-white">
                        <div className="mx-auto flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-6">
                                <div>
                                    <h1 className="text-lg font-semibold">Weekly Progress Entry</h1>
                                    <p className="truncate text-sm text-gray-600">{projectName}</p>
                                </div>
                                <Link to="/projects" className="rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-50">
                                    전체 프로젝트
                                </Link>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="hidden text-right sm:block">
                                    <div className="text-sm font-medium text-gray-800">{me?.name ?? 'User'}</div>
                                    <div className="text-xs text-gray-500">{me?.email ?? ''}</div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        logout();
                                        window.location.href = '/login?logged_out=1';
                                    }}
                                    className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
                                >
                                    로그아웃
                                </button>
                            </div>
                        </div>
                    </header>

                    <main className="mx-auto px-3 py-4 sm:px-6 sm:py-6">
                        {!currentWbsLoading && !hasCurrentWbs && (
                            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                현재 게시된 WBS가 없습니다.
                            </div>
                        )}

                        <WbsProgressEntryContainer
                            projectId={projectId}
                            canEdit={canEvEntry}
                            // canApprove={false}
                            // canAssign={false}
                            // mode="progress"
                            actorProjectCompanyId={actorProjectCompanyId}
                            // compact={true}
                        />
                    </main>
                </div>
            </ProjectSettingProvider>
        </ProjectAccessProvider>
    );
}
